import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useNominatedPersonnel } from '@/hooks/use-comply';
import { formatDateOnly } from '@/lib/date-utils';
import type { NominatedPerson, NominatedRole } from '@/lib/types';

const roleLabels: Record<NominatedRole, string> = {
  ramr: 'RAMR',
  technical_authority: 'Technical Authority',
  safety_officer: 'Safety Officer',
  quality_manager: 'Quality Manager',
  training_manager: 'Training Manager',
  primary_contact: 'Primary contact',
  equipment_selection: 'Selection of equipment',
  detailed_inspection: 'Detailed inspection of equipment',
  quarterly_safety_returns: 'Quarterly safety returns',
  communications: 'Communications / IRATA safety returns appointed person',
  other: 'Other',
};

const REQUIRED_ROLES: NominatedRole[] = [
  'ramr',
  'technical_authority',
  'primary_contact',
  'equipment_selection',
  'detailed_inspection',
  'quarterly_safety_returns',
];

function hasCertRequirement(person: NominatedPerson): boolean {
  return Boolean(person.required_cert_level && person.required_cert_level !== 'none');
}

function CertBadge({ person }: { person: NominatedPerson }) {
  if (!hasCertRequirement(person)) {
    return <Badge className="bg-gray-100 text-gray-700">No cert required</Badge>;
  }
  if (person.cert_validated) {
    return (
      <Badge className="bg-green-100 text-green-800">
        <ShieldCheck className="h-3 w-3 mr-1" />
        Validated
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-100 text-red-800">
      <ShieldX className="h-3 w-3 mr-1" />
      Not validated
    </Badge>
  );
}

function ActiveBadge({ isActive }: { isActive: boolean }) {
  return isActive ? (
    <Badge className="bg-green-100 text-green-800">Active</Badge>
  ) : (
    <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
  );
}

export function PersonnelPage() {
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');

  const { data: personnel, isLoading } = useNominatedPersonnel({
    role: roleFilter || undefined,
    is_active: activeFilter || undefined,
  });

  const validatedCount =
    personnel?.filter((p) => hasCertRequirement(p) && p.cert_validated).length ?? 0;
  const invalidCount =
    personnel?.filter((p) => hasCertRequirement(p) && !p.cert_validated).length ?? 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Nominated Personnel
          </h1>
          <p className="text-gray-600 mt-1">
            QP-300 Clause 2.4 — Appointed roles and certification status
          </p>
        </div>
        <Button asChild>
          <Link to="/compliance/personnel/new">
            <Plus className="h-4 w-4 mr-2" />
            Appoint Role
          </Link>
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{personnel?.length ?? '-'}</p>
                <p className="text-sm text-gray-600">Total Personnel</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={validatedCount > 0 ? '' : 'border-yellow-200'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{validatedCount}</p>
                <p className="text-sm text-gray-600">Certs Validated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={invalidCount > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold">{invalidCount}</p>
                <p className="text-sm text-gray-600">Certs Not Validated</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Select
          value={roleFilter || '_all'}
          onValueChange={(v) => setRoleFilter(v === '_all' ? '' : v)}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Roles</SelectItem>
            <SelectItem value="ramr">RAMR</SelectItem>
            <SelectItem value="technical_authority">Technical Authority</SelectItem>
            <SelectItem value="primary_contact">Primary contact</SelectItem>
            <SelectItem value="equipment_selection">Selection of equipment</SelectItem>
            <SelectItem value="detailed_inspection">Detailed inspection of equipment</SelectItem>
            <SelectItem value="quarterly_safety_returns">Quarterly safety returns</SelectItem>
            <SelectItem value="safety_officer">Safety Officer</SelectItem>
            <SelectItem value="quality_manager">Quality Manager</SelectItem>
            <SelectItem value="training_manager">Training Manager</SelectItem>
            <SelectItem value="communications">Communications / IRATA safety returns appointed person</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={activeFilter || '_all'}
          onValueChange={(v) => setActiveFilter(v === '_all' ? '' : v)}
        >
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : !personnel || personnel.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-600 font-medium">No nominated personnel yet</p>
            <p className="text-sm text-gray-500 mt-1">
              Appoint your first role to begin QP-300 compliance tracking
            </p>
            <Button asChild className="mt-4">
              <Link to="/compliance/personnel/new">
                <Plus className="h-4 w-4 mr-2" />
                Appoint Role
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Cert Status</TableHead>
                <TableHead className="hidden sm:table-cell">Appointment Date</TableHead>
                <TableHead className="hidden md:table-cell">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {personnel.map((person) => (
                <TableRow key={person.id} className="cursor-pointer hover:bg-gray-50">
                  <TableCell>
                    <span className="font-medium text-gray-900">
                      {person.name}
                    </span>
                    {person.email && (
                      <p className="text-xs text-gray-500">{person.email}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm">
                        {roleLabels[person.role] ?? person.role_display}
                      </span>
                      {REQUIRED_ROLES.includes(person.role) && (
                        <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 text-[10px] px-1.5 py-0">
                          Required
                        </Badge>
                      )}
                    </div>
                    {person.role_title !== person.role_display && (
                      <p className="text-xs text-gray-500">{person.role_title}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <CertBadge person={person} />
                    {hasCertRequirement(person) && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Requires: {person.required_cert_level.replace('_', ' ').toUpperCase()}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-gray-600">
                    {formatDateOnly(person.appointment_date)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <ActiveBadge isActive={person.is_active} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
